import type { ExecutiveSnapshotDto } from '@inference.engine/api-client';
import { AnalysisProseCard, AnalysisSubHeader } from '../../../components';
import { BorderedChipGrid } from './BorderedChipGrid';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../utils/hasContent';

interface MarketingMethodsStrategyProps {
  data?: ExecutiveSnapshotDto['marketingMethods'];
}

/** Whether any field this block renders has content; `marketingSpend` is not shown. */
export const hasMarketingMethods = (data: MarketingMethodsStrategyProps['data']) =>
  hasContent([data?.brandStrategy, data?.primaryChannels, data?.customerAcquisition]);

export const MarketingMethodsStrategy = ({ data }: MarketingMethodsStrategyProps) => {
  const { t } = useTranslation('analysis');

  if (!data || !hasMarketingMethods(data)) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.marketingMethodsStrategy.marketingMethodsStrategy')}
        subtitle={t('analysisResult.marketingMethodsStrategy.brandApproachChannelsAcquisition')}
        subtitleTone="meta"
      />

      {data.brandStrategy && (
        <AnalysisProseCard
          title={t('analysisResult.marketingMethodsStrategy.brandStrategy')}
          titleVariant="heading"
          headingLevel="h3"
          description={data.brandStrategy}
          columns={2}
        />
      )}

      {data.primaryChannels && data.primaryChannels.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <h3 className="text-heading-sm font-medium text-foreground">
            {t('analysisResult.marketingMethodsStrategy.primaryMarketingChannels')}
          </h3>
          <BorderedChipGrid
            items={data.primaryChannels}
            columns={4}
            badgeLabel={(number) =>
              t('analysisResult.marketingMethodsStrategy.channelNumber', { number })
            }
          />
        </div>
      )}

      {data.customerAcquisition && data.customerAcquisition.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <h3 className="text-heading-sm font-medium text-foreground">
            {t('analysisResult.marketingMethodsStrategy.customerAcquisition')}
          </h3>
          <BorderedChipGrid items={data.customerAcquisition} columns={2} />
        </div>
      )}
    </div>
  );
};
